/**
 * 차단(gate_blocked) 룰 승인 확인 창 body (D15).
 *
 * - `provenance.gate_issues`를 전부 나열한다. 비어 있으면 확인 필요 문구.
 * - "승인하면 이 룰과 전개본 N건이 함께 승인됩니다." N = archived가 아닌 전개본 수(상위 계산).
 * - 버튼 [취소] [이슈를 확인했고 승인]. 확인 버튼은 누르는 즉시 비활성화되고 요청이 끝날 때까지
 *   다시 눌리지 않는다(ref 가드 — 연타로 PATCH가 두 번 나가는 것을 막는다).
 *
 * ConfirmManager.show({ body: <GateApproveConfirm .../> })로 띄운다(버튼을 body가 직접 그림).
 */
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import { GATE_ISSUES_EMPTY_TEXT } from "./GateReasonBox";

export interface GateApproveConfirmProps {
  gateIssues: string[];
  /** archived가 아닌 전개본 수(동반 전이 대상, D10·D15). */
  derivedActiveCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}

export const GATE_APPROVE_LABEL = "이슈를 확인했고 승인";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 360px;
  max-width: 520px;
`;

const Lead = styled.div`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const IssueTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Red")?.[700]};
`;

const IssueList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Red")?.[700]};
`;

const Cascade = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const GateApproveConfirm: React.FC<GateApproveConfirmProps> = ({
  gateIssues,
  derivedActiveCount,
  onCancel,
  onConfirm,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  const handleConfirm = () => {
    if (submittedRef.current) return; // 연타 방지: 정확히 1회만 PATCH
    submittedRef.current = true;
    setSubmitting(true);
    onConfirm();
  };

  return (
    <Wrap>
      <Lead>
        이 룰은 추출 검사에 걸려 차단(gate_blocked)됐습니다. 아래 검사 이슈를 확인한 뒤 승인합니다.
      </Lead>
      <IssueTitle>차단 사유 {gateIssues.length}건</IssueTitle>
      {gateIssues.length > 0 ? (
        <IssueList>
          {gateIssues.map((issue, i) => (
            <li key={i}>{issue}</li>
          ))}
        </IssueList>
      ) : (
        <Empty>{GATE_ISSUES_EMPTY_TEXT}</Empty>
      )}
      <Cascade>승인하면 이 룰과 전개본 {derivedActiveCount}건이 함께 승인됩니다.</Cascade>
      <Buttons>
        <Button
          buttonType="button"
          type="Text"
          size="Medium"
          variant="Ghost"
          color="Secondary"
          disabled={submitting}
          disabledColor
          onClick={onCancel}
        >
          취소
        </Button>
        <Button
          buttonType="button"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          disabled={submitting}
          disabledColor
          onClick={handleConfirm}
        >
          {GATE_APPROVE_LABEL}
        </Button>
      </Buttons>
    </Wrap>
  );
};

export default GateApproveConfirm;
